import { BUSINESS_TYPE_LABELS, BUSINESS_TYPES, emailSchema, passwordSchema } from '@crm/shared';
import { Button, cn, Input, Label, RadioGroup, Select, Spinner } from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Eye, EyeOff, Loader2, X } from 'lucide-react';
import { type ClipboardEvent, type FormEvent, useEffect, useRef, useState } from 'react';
import { type UseFormRegisterReturn, useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormError, FormField } from '@/components';
import { ApiError, apiClient } from '@/lib/api-client';
import { useCompleteLead, useRequestLeadCode, useVerifyLeadCode } from '../api';
import { WizardFooter } from './wizard-footer';

const TIPOS_DISPONIVEIS = BUSINESS_TYPES.filter((type) => type !== 'DENTAL_CLINIC');

const initialSchema = z
  .object({
    profileType: z.enum(['ESTABELECIMENTO', 'AUTONOMO']),
    name: z.string().trim().min(2, 'Nome muito curto').max(80),
    whatsappDdd: z
      .string()
      .trim()
      .regex(/^\d{2}$/, 'Informe o DDD com 2 digitos'),
    whatsappNumber: z
      .string()
      .trim()
      .regex(/^\d{8,9}$/, 'Informe um numero de 8 ou 9 digitos'),
    responsibleName: z.string().trim().optional(),
    businessType: z.enum(BUSINESS_TYPES).optional(),
    document: z.string().trim().max(20).regex(/^\d*$/, 'Informe apenas numeros').optional(),
    legalName: z.string().trim().max(160).optional(),
  })
  .superRefine((values, context) => {
    if (values.profileType !== 'ESTABELECIMENTO') return;
    if (!values.responsibleName) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['responsibleName'],
        message: 'Informe o nome do responsavel',
      });
    }
    if (!values.businessType) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['businessType'],
        message: 'Selecione o tipo de estabelecimento',
      });
    }
  });

const locationSchema = z.object({
  instagram: z
    .string()
    .trim()
    .regex(/^@?[A-Za-z0-9._]{1,30}$/, 'Informe um @ do Instagram valido'),
  city: z.string().trim().min(1, 'Informe a cidade').max(120),
  state: z.string().trim().min(1, 'Informe o estado').max(60),
});

const detailsSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Confirme a senha'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'As senhas nao coincidem',
    path: ['confirmPassword'],
  });

const ESTADOS = [
  ['AC', 'Acre'],
  ['AL', 'Alagoas'],
  ['AP', 'Amapá'],
  ['AM', 'Amazonas'],
  ['BA', 'Bahia'],
  ['CE', 'Ceará'],
  ['DF', 'Distrito Federal'],
  ['ES', 'Espírito Santo'],
  ['GO', 'Goiás'],
  ['MA', 'Maranhão'],
  ['MT', 'Mato Grosso'],
  ['MS', 'Mato Grosso do Sul'],
  ['MG', 'Minas Gerais'],
  ['PA', 'Pará'],
  ['PB', 'Paraíba'],
  ['PR', 'Paraná'],
  ['PE', 'Pernambuco'],
  ['PI', 'Piauí'],
  ['RJ', 'Rio de Janeiro'],
  ['RN', 'Rio Grande do Norte'],
  ['RS', 'Rio Grande do Sul'],
  ['RO', 'Rondônia'],
  ['RR', 'Roraima'],
  ['SC', 'Santa Catarina'],
  ['SP', 'São Paulo'],
  ['SE', 'Sergipe'],
  ['TO', 'Tocantins'],
] as const;

type InitialValues = z.infer<typeof initialSchema>;
type LocationValues = z.infer<typeof locationSchema>;
type DetailsValues = z.infer<typeof detailsSchema>;
type Stage = 'initial' | 'otp' | 'location' | 'details';
type InstagramStatus = 'idle' | 'checking' | 'valid' | 'invalid';

const otpSlots = ['one', 'two', 'three', 'four', 'five', 'six'] as const;

function OtpFields({ value, onChange }: { value: string[]; onChange: (value: string[]) => void }) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  function update(index: number, raw: string) {
    const digits = raw.replace(/\D/g, '');
    const next = [...value];
    digits
      .slice(0, 6 - index)
      .split('')
      .forEach((digit, offset) => {
        next[index + offset] = digit;
      });
    onChange(next);
    refs.current[Math.min(index + Math.max(digits.length, 1), 5)]?.focus();
  }

  function paste(event: ClipboardEvent<HTMLInputElement>, index: number) {
    event.preventDefault();
    update(index, event.clipboardData.getData('text'));
  }

  return (
    <fieldset className="flex justify-center gap-2 sm:gap-3">
      <legend className="sr-only">Código de confirmação</legend>
      {value.map((digit, index) => (
        <input
          key={otpSlots[index]}
          ref={(element) => {
            refs.current[index] = element;
          }}
          value={digit}
          onChange={(event) => update(index, event.target.value)}
          onPaste={(event) => paste(event, index)}
          onKeyDown={(event) => {
            if (event.key === 'Backspace' && !value[index] && index > 0) {
              refs.current[index - 1]?.focus();
            }
          }}
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          aria-label={`Dígito ${index + 1} do código`}
          className="!h-14 !w-11 rounded-lg border border-input bg-muted/50 text-center !text-2xl font-semibold focus-visible:border-primary focus-visible:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 sm:!h-16 sm:!w-14 sm:!text-3xl"
        />
      ))}
    </fieldset>
  );
}

function WhatsappFields({
  label,
  dddField,
  numberField,
  dddError,
  numberError,
}: {
  label: string;
  dddField: UseFormRegisterReturn;
  numberField: UseFormRegisterReturn;
  dddError?: string;
  numberError?: string;
}) {
  const numberRef = useRef<HTMLInputElement | null>(null);

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{label}</p>
      <div className="grid grid-cols-[5rem_1fr] gap-2">
        <FormField
          label="DDD"
          inputMode="numeric"
          maxLength={2}
          error={dddError}
          {...dddField}
          onChange={(event) => {
            event.currentTarget.value = event.currentTarget.value.replace(/\D/g, '').slice(0, 2);
            dddField.onChange(event);
            if (event.currentTarget.value.length === 2) numberRef.current?.focus();
          }}
        />
        <FormField
          label="Número"
          type="tel"
          inputMode="numeric"
          maxLength={9}
          placeholder="99999-9999"
          error={numberError}
          {...numberField}
          ref={(element) => {
            numberField.ref(element);
            numberRef.current = element;
          }}
          onChange={(event) => {
            event.currentTarget.value = event.currentTarget.value.replace(/\D/g, '').slice(0, 9);
            numberField.onChange(event);
          }}
        />
      </div>
    </div>
  );
}

/** Label + input de senha com botao mostrar/ocultar - so troca o `type`. */
function PasswordField({
  id,
  label,
  error,
  placeholder,
  autoComplete,
  field,
}: {
  id: string;
  label: string;
  error?: string;
  placeholder?: string;
  autoComplete?: string;
  field: UseFormRegisterReturn;
}) {
  const [show, setShow] = useState(false);

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          className={cn('pr-10', error && 'border-destructive focus-visible:ring-destructive')}
          {...field}
        />
        <button
          type="button"
          onClick={() => setShow((atual) => !atual)}
          aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function forcaSenha(senha: string): { nivel: 0 | 1 | 2 | 3; label: string } {
  if (!senha) return { nivel: 0, label: '' };

  let pontos = 0;
  if (senha.length >= 8) pontos++;
  if (senha.length >= 12) pontos++;
  if (/[a-z]/.test(senha) && /[A-Z]/.test(senha)) pontos++;
  if (/\d/.test(senha)) pontos++;
  if (/[^A-Za-z0-9]/.test(senha)) pontos++;

  if (pontos <= 1) return { nivel: 1, label: 'Fraca' };
  if (pontos <= 3) return { nivel: 2, label: 'Media' };
  return { nivel: 3, label: 'Forte' };
}

function IndicadorSenha({ senha }: { senha: string }) {
  const { nivel, label } = forcaSenha(senha);
  if (!senha) return null;

  const cor = nivel === 1 ? 'bg-destructive' : nivel === 2 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div className="flex items-center gap-2">
      <div className="flex flex-1 gap-1">
        {[1, 2, 3].map((segmento) => (
          <div
            key={segmento}
            className={`h-1.5 flex-1 rounded-full ${segmento <= nivel ? cor : 'bg-muted'}`}
          />
        ))}
      </div>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

/** Confirma visualmente se as duas senhas batem, assim que a segunda tem conteudo -
    nao espera o submit pra dizer que deu certo. */
function ConfirmacaoSenha({ bateu }: { bateu: boolean | null }) {
  if (bateu === null) return null;

  return bateu ? (
    <p className="flex items-center gap-1 text-xs text-emerald-600">
      <Check className="h-3.5 w-3.5" /> As senhas coincidem
    </p>
  ) : null;
}

function isDuplicateError(error: unknown) {
  return error instanceof ApiError && error.code === 'EMAIL_ALREADY_IN_USE';
}

async function verificarInstagram(handle: string): Promise<boolean> {
  const { exists } = await apiClient.get<{ exists: boolean }>(
    `/api/v1/signup/instagram/check?handle=${encodeURIComponent(handle)}`,
  );
  return exists;
}

export function CompanyLeadForm({ onEmailTaken }: { onEmailTaken: () => void }) {
  const requestCode = useRequestLeadCode();
  const verifyCode = useVerifyLeadCode();
  const completeLead = useCompleteLead();
  const [stage, setStage] = useState<Stage>('initial');
  const [profileType, setProfileType] = useState<InitialValues['profileType']>('ESTABELECIMENTO');
  const [verificationWhatsapp, setVerificationWhatsapp] = useState('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [otp, setOtp] = useState<string[]>(Array.from({ length: 6 }, () => ''));
  const [location, setLocation] = useState<LocationValues | null>(null);
  const [instagramStatus, setInstagramStatus] = useState<InstagramStatus>('idle');

  const initialForm = useForm<InitialValues>({
    resolver: zodResolver(initialSchema),
    defaultValues: {
      profileType: 'ESTABELECIMENTO',
      name: '',
      whatsappDdd: '',
      whatsappNumber: '',
      responsibleName: '',
      businessType: 'BEAUTY_SALON',
      document: '',
      legalName: '',
    },
  });
  const locationForm = useForm<LocationValues>({
    resolver: zodResolver(locationSchema),
    defaultValues: { instagram: '', city: '', state: '' },
  });
  const detailsForm = useForm<DetailsValues>({
    resolver: zodResolver(detailsSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
  });
  const senha = detailsForm.watch('password');
  const confirmarSenha = detailsForm.watch('confirmPassword');
  const instagramDigitado = locationForm.watch('instagram');

  // Ao digitar o Instagram, confere se o perfil existe de verdade - a mesma
  // regra que `completeSignup` aplica no fim, so que aqui vira feedback
  // enquanto a pessoa ainda esta preenchendo, nao um erro so no fim.
  useEffect(() => {
    const handle = instagramDigitado?.trim();
    if (!handle || !/^@?[A-Za-z0-9._]{1,30}$/.test(handle)) {
      setInstagramStatus('idle');
      return;
    }
    setInstagramStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const existe = await verificarInstagram(handle);
        setInstagramStatus(existe ? 'valid' : 'invalid');
      } catch {
        setInstagramStatus('idle');
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [instagramDigitado]);

  function submitInitial(values: InitialValues) {
    const companyWhatsapp = `${values.whatsappDdd}${values.whatsappNumber}`;
    setVerificationWhatsapp(companyWhatsapp);
    requestCode.mutate(
      {
        name: values.name,
        whatsapp: companyWhatsapp,
        profileType: values.profileType,
        responsibleName: values.responsibleName || values.name,
        responsibleWhatsapp: companyWhatsapp,
        ...(values.profileType === 'ESTABELECIMENTO'
          ? {
              businessType: values.businessType,
              document: values.document,
              legalName: values.legalName,
            }
          : {}),
      },
      {
        onSuccess: (data) => {
          setVerificationId(data.verificationId);
          setStage('otp');
        },
        onError: (error) => {
          if (isDuplicateError(error)) onEmailTaken();
        },
      },
    );
  }

  function submitOtp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!verificationId || !verificationWhatsapp) return;
    const code = otp.join('');
    if (code.length !== 6) return;

    verifyCode.mutate(
      { verificationId, whatsapp: verificationWhatsapp, code },
      { onSuccess: () => setStage('location') },
    );
  }

  function submitLocation(values: LocationValues) {
    if (instagramStatus === 'invalid') return;
    setLocation(values);
    setStage('details');
  }

  function submitDetails(values: DetailsValues) {
    if (!verificationId || !location) return;

    completeLead.mutate(
      {
        verificationId,
        email: values.email,
        password: values.password,
        instagram: location.instagram,
        city: location.city,
        state: location.state,
      },
      {
        onError: (error) => {
          if (isDuplicateError(error)) onEmailTaken();
        },
      },
    );
  }

  const erro = requestCode.error ?? verifyCode.error ?? completeLead.error;
  const erroGenerico = isDuplicateError(erro) ? null : erro;

  if (stage === 'initial') {
    const nomeLabel =
      profileType === 'AUTONOMO'
        ? 'Te conhecem por qual nome?'
        : 'Qual o nome do seu estabelecimento?';
    const nomePlaceholder =
      profileType === 'AUTONOMO' ? 'ex.: Mary Cabeleireira' : 'ex.: Salão Studio Hair';
    const whatsappLabel = 'Qual o WhatsApp?';
    const {
      register,
      formState: { errors },
    } = initialForm;

    return (
      <form
        className="space-y-4 pb-20 sm:pb-0"
        onSubmit={initialForm.handleSubmit(submitInitial)}
        noValidate
      >
        <FormError message={erroGenerico ? erroGenerico.message : null} />
        <RadioGroup
          label="Tipo de cadastro"
          variant="cards"
          value={profileType}
          onChange={(value) => {
            setProfileType(value);
            initialForm.setValue('profileType', value);
            // Nome e whatsapp tem significado diferente em cada modo (pessoa
            // x estabelecimento) - trocar de tipo sem limpar deixava o nome
            // da pessoa preenchido como nome do estabelecimento.
            initialForm.setValue('name', '');
            initialForm.setValue('whatsappDdd', '');
            initialForm.setValue('whatsappNumber', '');
          }}
          options={[
            { value: 'ESTABELECIMENTO', label: 'Tenho um estabelecimento' },
            { value: 'AUTONOMO', label: 'Sou profissional autonomo' },
          ]}
        />
        {profileType === 'ESTABELECIMENTO' ? (
          <div className="space-y-2">
            <Label htmlFor="businessType-lead">Tipo de Estabelecimento</Label>
            <Select id="businessType-lead" {...register('businessType')}>
              {TIPOS_DISPONIVEIS.map((type) => (
                <option key={type} value={type}>
                  {BUSINESS_TYPE_LABELS[type]}
                </option>
              ))}
            </Select>
            {errors.businessType ? (
              <p className="text-xs text-destructive">{errors.businessType.message}</p>
            ) : null}
          </div>
        ) : null}
        <FormField
          label={nomeLabel}
          placeholder={nomePlaceholder}
          error={errors.name?.message}
          {...register('name')}
        />
        <WhatsappFields
          label={whatsappLabel}
          dddField={register('whatsappDdd')}
          numberField={register('whatsappNumber')}
          dddError={errors.whatsappDdd?.message}
          numberError={errors.whatsappNumber?.message}
        />
        {profileType === 'ESTABELECIMENTO' ? (
          <>
            <FormField
              label="Nome do(a) dono(a) ou responsável"
              error={errors.responsibleName?.message}
              {...register('responsibleName')}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                label="CNPJ (opcional)"
                placeholder="Somente numeros"
                error={errors.document?.message}
                {...register('document')}
              />
              <FormField
                label="Razao social (opcional)"
                error={errors.legalName?.message}
                {...register('legalName')}
              />
            </div>
          </>
        ) : null}
        <WizardFooter justify="center">
          <Button type="submit" className="w-full sm:w-1/2" disabled={requestCode.isPending}>
            {requestCode.isPending ? <Spinner /> : null}
            Confirmar
          </Button>
        </WizardFooter>
      </form>
    );
  }

  if (stage === 'otp') {
    return (
      <form className="space-y-6 pb-20 sm:pb-0" onSubmit={submitOtp} noValidate>
        <FormError message={erroGenerico ? erroGenerico.message : null} />
        <p className="text-center text-sm text-muted-foreground">
          Digite o código que enviamos para seu WhatsApp
        </p>
        <OtpFields value={otp} onChange={setOtp} />
        <WizardFooter justify="center">
          <Button
            type="submit"
            className="w-full sm:w-1/2"
            disabled={verifyCode.isPending || otp.join('').length !== 6}
          >
            {verifyCode.isPending ? <Spinner /> : null}
            Avançar
          </Button>
        </WizardFooter>
      </form>
    );
  }

  if (stage === 'location') {
    return (
      <form
        className="space-y-4 pb-20 sm:pb-0"
        onSubmit={locationForm.handleSubmit(submitLocation)}
        noValidate
      >
        <div className="space-y-2">
          <Label htmlFor="instagram-lead">Instagram</Label>
          <div className="relative">
            <Input
              id="instagram-lead"
              autoComplete="off"
              placeholder="@seuperfil"
              className="pr-9"
              aria-invalid={
                locationForm.formState.errors.instagram || instagramStatus === 'invalid'
                  ? true
                  : undefined
              }
              {...locationForm.register('instagram')}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              {instagramStatus === 'checking' ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : instagramStatus === 'valid' ? (
                <Check className="h-4 w-4 text-emerald-600" />
              ) : instagramStatus === 'invalid' ? (
                <X className="h-4 w-4 text-destructive" />
              ) : null}
            </span>
          </div>
          {locationForm.formState.errors.instagram ? (
            <p className="text-xs text-destructive">
              {locationForm.formState.errors.instagram.message}
            </p>
          ) : instagramStatus === 'invalid' ? (
            <p className="text-xs text-destructive">Não encontramos esse perfil do Instagram</p>
          ) : instagramStatus === 'valid' ? (
            <p className="text-xs text-emerald-600">Perfil encontrado</p>
          ) : null}
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <FormField
              label="Cidade"
              error={locationForm.formState.errors.city?.message}
              {...locationForm.register('city')}
            />
          </div>
          <div className="w-full space-y-2 sm:w-auto">
            <Label htmlFor="estado-lead">Estado</Label>
            <Select id="estado-lead" className="sm:w-auto" {...locationForm.register('state')}>
              <option value="" disabled>
                Selecione...
              </option>
              {ESTADOS.map(([sigla, nome]) => (
                <option key={sigla} value={sigla}>
                  {nome}
                </option>
              ))}
            </Select>
            {locationForm.formState.errors.state ? (
              <p className="text-xs text-destructive">
                {locationForm.formState.errors.state.message}
              </p>
            ) : null}
          </div>
        </div>
        <WizardFooter justify="center">
          <Button
            type="submit"
            className="w-full sm:w-1/2"
            disabled={instagramStatus === 'checking' || instagramStatus === 'invalid'}
          >
            Avançar
          </Button>
        </WizardFooter>
      </form>
    );
  }

  return (
    <form
      className="space-y-4 pb-20 sm:pb-0"
      onSubmit={detailsForm.handleSubmit(submitDetails)}
      noValidate
    >
      <FormError message={erroGenerico ? erroGenerico.message : null} />
      <FormField
        label="E-mail"
        type="email"
        autoComplete="email"
        placeholder="voce@empresa.com"
        error={detailsForm.formState.errors.email?.message}
        {...detailsForm.register('email')}
      />
      <div className="space-y-1">
        <PasswordField
          id="password-lead"
          label="Senha"
          placeholder="Minimo 8 caracteres"
          autoComplete="new-password"
          error={detailsForm.formState.errors.password?.message}
          field={detailsForm.register('password')}
        />
        <IndicadorSenha senha={senha ?? ''} />
      </div>
      <div className="space-y-1">
        <PasswordField
          id="confirm-password-lead"
          label="Confirmar senha"
          placeholder="Repita a senha"
          autoComplete="new-password"
          error={detailsForm.formState.errors.confirmPassword?.message}
          field={detailsForm.register('confirmPassword')}
        />
        <ConfirmacaoSenha bateu={confirmarSenha ? senha === confirmarSenha : null} />
      </div>
      <WizardFooter justify="center">
        <Button type="submit" className="w-full sm:w-1/2" disabled={completeLead.isPending}>
          {completeLead.isPending ? <Spinner /> : null}
          Avançar
        </Button>
      </WizardFooter>
    </form>
  );
}
